import * as React from 'react';
import { reduxForm, Field, InjectedFormProps } from 'redux-form';

import { getUSAStates } from '../../utils/ws';

import { required, email, website, notFirstAndLastName } from '../../utils/validators';

import TextField from '../form/MaterialTextInput';
import SelectField from '../form/MaterialSelectInput';
import MaskedTextInput from '../form/MaterialMaskedTextInput';
import { LoadingSpinner, RECAPTCHA_SITE_KEY, StorableDataKeys } from '@weddingspot/ws-core-components';
import CheckboxInput from '../form/MaterialCheckboxInput';

export interface FormData {
    name: string;
    email: string;
    venue_name: string;
    phone_number: string;
    website: string;
    state: string;
    captcha: boolean;
    grecaptcha_response: string;
}

const requiredValidation = (errorMessage: string): ((value: string | undefined) => undefined | string) => {
    return (value: string | undefined) => (required(value) ? errorMessage : undefined);
};

const requiredName = requiredValidation('Please enter your first and last name');
const validateName = (value: string | undefined) => (notFirstAndLastName(value) ? 'Please enter both your first and last name' : undefined);

const requiredEmail = requiredValidation('Please enter a valid email address');
const validateEmail = (value: string | undefined) => (email(value) ? 'Please enter a valid email address' : undefined);

const requiredVenueName = requiredValidation('Please enter your venue name');

const requiredPhone = (value: string | undefined) => (required(value) ? 'A phone number is required' : undefined);
const requiredWebsite = (value: string | undefined) => (required(value) || website(value) ? 'Please enter a valid website' : undefined);

const requiredState = requiredValidation('Please choose which state your venue is in');

const requiredCaptcha = (value: string | undefined) => (required(value) ? 'Captcha verification failed' : undefined);

class SignupForm extends React.Component<InjectedFormProps, {}> {
    
    componentDidMount() {
        const grecaptcha = document.createElement('script');
        grecaptcha.type = 'text/javascript';
        grecaptcha.async = true;
        grecaptcha.defer = true;
        grecaptcha.src = 'https://www.google.com/recaptcha/api.js?onload=recaptchaCallbackVendorSignupForm&render=explicit';
        document.head.appendChild(grecaptcha);

        const callbackMethod = document.createElement('script');
        callbackMethod.type = 'text/javascript';
        callbackMethod.text = `
        var recaptchaCallbackVendorSignupForm = function () {
            let recaptcha_widget_id_vendor_signup_form = grecaptcha.render('vendor_signup_form_recaptcha',{
                'sitekey':'${RECAPTCHA_SITE_KEY}', 
                'callback': function (response) {
                    document.getElementById('vendor-signup-form-hidden-input').click();
                },
                'expired-callback': function (response) {
                    document.getElementById('vendor-signup-form-hidden-input').click();
                }
            })
            localStorage.setItem('${StorableDataKeys.RECAPTCHA_WIDGET_ID_VENDOR_SIGNUP_FORM}', recaptcha_widget_id_vendor_signup_form);
        };
        `
        document.head.appendChild(callbackMethod);
    }
    
    render() {
        const { handleSubmit, pristine, submitting } = this.props;
        return (
            <form onSubmit={handleSubmit}>
                <div className='signup-form-content'>
                    <div className='signup-form-content__description'>
                        Fill out the information below to begin getting your venue listing set up.
                    </div>
                    <Field name='name' validate={[requiredName, validateName]} label='Name' component={TextField} placeholder='Name' />
                    <Field name='email' validate={[requiredEmail, validateEmail]} label='Email' placeholder='Email' component={TextField} />

                    <div className='row'>
                        <Field
                            name='phone_number'
                            label='Phone number'
                            placeholder='Phone number'
                            validate={[requiredPhone]}
                            component={MaskedTextInput}
                            {...{
                                mask: [
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    '-',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    '-',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    ' ',
                                    'e',
                                    'x',
                                    't',
                                    '.',
                                    ' ',
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                    /\d/,
                                ],
                            }}
                        />
                        <div className='row__spacer' />
                        <Field
                            name='state'
                            validate={[requiredState]}
                            placeholder='State'
                            label='State'
                            component={SelectField}
                            {...{
                                choices: getUSAStates() as [string, string][],
                            }}
                        />
                    </div>
                    <div className='row'>
                        <Field
                            name='venue_name'
                            validate={[requiredVenueName]}
                            placeholder='Venue name'
                            label='Venue name'
                            component={TextField}
                        />
                        <div className='row__spacer' />
                        <Field
                            name='website'
                            label='Venue website'
                            placeholder='Venue website'
                            validate={[requiredWebsite]}
                            component={TextField}
                        />
                    </div>

                    <div className='captcha-container'>
                        <div id='vendor_signup_form_recaptcha'/>  
                    </div>

                    <Field
                        hidden={true}
                        name='captcha'
                        id={'vendor-signup-form-hidden-input'}
                        component={CheckboxInput}
                        validate={[requiredCaptcha]}
                    />
                </div>

                <div className='signup-form-button-container'>
                    <div className='signup-form-privacy-text'>
                        {'By signing up, you agree to our '}
                        <a href='/terms-of-service/' target='_blank'>
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href='/privacy/' target='_blank'>
                            Privacy Policy
                        </a>
                    </div>
                    <button type='submit' className='signup-form-button-container__button Button' disabled={pristine || submitting}>
                        COMPLETE SIGN UP
                    </button>
                    {submitting && <LoadingSpinner />}
                </div>
            </form>
        );
    }
}

export { requiredName, requiredEmail, requiredPhone, requiredWebsite, requiredState, requiredCaptcha };

export default reduxForm({
    form: 'SignupForm',
})(SignupForm);
